import { useState, useRef, useEffect } from "react";
import { BookingDialog, type BookingDialogEvent } from "./BookingDialog";
import { clampStartToNow, quickBookDurations, resolveQuickBooking } from "./time";
import { localDateKey, pastSlotThreshold as computePastSlotThreshold } from "./scheduleTime";
import { estimateCharge, type RoomPricing } from "./pricing";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScheduleEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  organizerName?: string;
  isMyBooking?: boolean;
  description?: string;
}

export interface ScheduleFreeSlot {
  start: string;
  end: string;
  durationMinutes: number;
}

export interface RoomDayScheduleProps {
  events: ScheduleEvent[];
  freeSlots: ScheduleFreeSlot[];
  /** Date for building ISO timestamps (defaults to today) */
  date?: Date;
  /** Date header text (e.g. "Monday, April 9") */
  dateLabel?: string;
  isToday?: boolean;
  /** Quick-book durations in minutes */
  bookDurations?: number[];
  onBook?: (start: string, end: string, title: string, description: string) => void;
  onCancel?: (eventId: number) => void;
  onPrevDay?: () => void;
  onNextDay?: () => void;
  /** Called when a date is picked from the calendar popup */
  onDateChange?: (date: Date) => void;
  /** Compact mode for sidebar (narrower, no day nav) */
  compact?: boolean;
  className?: string;
  /** Optional line under the booking time, e.g. the estimated charge of a paid room.
   *  When it returns a non-empty string for a free-slot quick chip's slot, the
   *  chip opens the BookingDialog (estimate + confirm) instead of booking on one tap. */
  priceNote?: (start: string, end: string) => string | null;
  /** Optional warning shown when confirming a cancellation, e.g. a late-cancellation charge. */
  cancelNote?: (event: BookingDialogEvent) => string | null;
  /** The room's pricing (meeting-room charging), passed to the BookingDialog (cost summary + terms,
   *  late-cancellation warning). A quick-book chip whose slot would cost something opens the dialog
   *  instead of booking on one tap. */
  pricing?: RoomPricing | null;
  /** Optional line under the cost, e.g. "will be sent to your company admin for approval" (#401). Shown with or
   *  without pricing — unlike ``priceNote``. */
  bookingNotice?: (start: string, end: string) => string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;
const SCROLL_TO_HOUR = 6;
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const PX_PER_MINUTE = 2;
const SLOT_MINUTES = 15;
const TOTAL_SLOTS = TOTAL_MINUTES / SLOT_MINUTES;
const SLOT_HEIGHT = SLOT_MINUTES * PX_PER_MINUTE;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function minutesFromDayStart(iso: string): number {
  const dt = new Date(iso);
  return dt.getHours() * 60 + dt.getMinutes() - DAY_START_HOUR * 60;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EventBlock({ event, onClick }: { event: ScheduleEvent; onClick?: (e: React.MouseEvent) => void }) {
  const startMins = minutesFromDayStart(event.start);
  let endMins = minutesFromDayStart(event.end);
  if (endMins <= startMins) endMins = TOTAL_MINUTES;
  const top = Math.max(0, startMins) * PX_PER_MINUTE;
  const height = Math.max(2, (Math.min(endMins, TOTAL_MINUTES) - Math.max(startMins, 0))) * PX_PER_MINUTE;

  return (
    <div
      onClick={onClick}
      style={{
        position: "absolute", left: 4, right: 4, top, height,
        borderRadius: 4, padding: "2px 6px", overflow: "hidden",
        background: event.isMyBooking ? "rgba(59,130,246,0.15)" : "rgba(239,68,68,0.15)",
        border: `1px solid ${event.isMyBooking ? "rgba(59,130,246,0.4)" : "rgba(239,68,68,0.4)"}`,
        cursor: onClick ? "pointer" : "default",
        zIndex: 5,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {event.title || "Booked"}
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8" }}>
        {formatTime(event.start)} – {formatTime(event.end)}
      </div>
      {event.organizerName && (
        <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {event.organizerName}
        </div>
      )}
    </div>
  );
}

function FreeSlotBlock({ slot, durations, onBook, nowMinutes }: { slot: ScheduleFreeSlot; durations: number[]; onBook?: (start: string, end: string, x: number, y: number) => void; nowMinutes?: number }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const startMins = minutesFromDayStart(slot.start);
  let endMins = minutesFromDayStart(slot.end);
  // Handle midnight next day (00:00 wraps to 0 — treat as end-of-day)
  if (endMins <= startMins) endMins = TOTAL_MINUTES;
  // Clip to current time — don't show green for past portions
  const visibleStart = nowMinutes != null ? Math.max(startMins, nowMinutes) : startMins;
  if (visibleStart >= endMins) return null; // Entirely in the past
  const top = Math.max(0, visibleStart) * PX_PER_MINUTE;
  const height = Math.max(2, (Math.min(endMins, TOTAL_MINUTES) - Math.max(visibleStart, 0))) * PX_PER_MINUTE;
  // The clock this block was last drawn against. The press re-reads it, so a
  // schedule left open on a kiosk books from the moment of the press rather
  // than from whenever it last rendered (#344), and storing the fresh reading
  // redraws the row so a button the slot has since outgrown stops being
  // offered. Durations are measured from where the booking would start, not
  // from the slot's nominal start — the same rule as the standalone
  // ``QuickBookButtons``, which is the same control to a member's eye.
  const available = quickBookDurations(durations, slot.start, slot.durationMinutes, nowMs);

  return (
    <div
      style={{
        position: "absolute", left: 4, right: 4, top, height,
        background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)",
        borderRadius: 4, padding: "4px 6px", overflow: "hidden",
        display: "flex", flexDirection: "column", gap: 4,
        // Sits above the selectable-slot grid (``zIndex:1``) so the
        // quick-book buttons inside actually receive clicks. BUT the
        // container is ``pointerEvents:"none"`` so a mousedown on the
        // green free area falls THROUGH to the grid cell underneath and
        // starts a drag-to-book selection — only the buttons below
        // re-enable pointer events. Without this the overlay swallowed
        // the drag and you could only book the preselected durations.
        zIndex: 2,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {height >= 28 && (
        <div style={{ fontSize: 12, fontWeight: 500, color: "#22c55e" }}>
          Free · {formatDuration(slot.durationMinutes)}
        </div>
      )}
      {height >= 52 && available.length > 0 && onBook && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {available.map((d) => {
            const book = (x: number, y: number) => {
              const pressedAt = Date.now();
              setNowMs(pressedAt);
              const booking = resolveQuickBooking(slot.start, slot.durationMinutes, d, pressedAt);
              if (booking) onBook(booking.start, booking.end, x, y);
            };
            return (
              <button
                key={d}
                type="button"
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  book(rect.right + 8, rect.top);
                }}
                style={{
                  fontSize: 11, background: "#22c55e", color: "#fff",
                  padding: "2px 6px", borderRadius: 4, border: "none", cursor: "pointer",
                  // Re-enable pointer events (the container sets
                  // ``pointerEvents:"none"`` so drags fall through to the
                  // grid) so the quick-book buttons still receive clicks.
                  pointerEvents: "auto",
                  // Without ``userSelect:"none"`` the text inside the
                  // button picks up the parent timeline's text-select
                  // cursor on hover (the grid sets ``cursor:"crosshair"``
                  // / ``cursor:"text"`` depending on state) — buttons
                  // should always read as clickable.
                  userSelect: "none",
                }}
              >
                {d}m
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RoomDaySchedule({
  events,
  freeSlots,
  dateLabel,
  isToday,
  bookDurations = [15, 30, 60],
  onBook,
  onCancel,
  onPrevDay,
  onNextDay,
  onDateChange,
  compact,
  className,
  date,
  priceNote,
  cancelNote,
  pricing,
  bookingNotice,
}: RoomDayScheduleProps) {
  const calendarRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [selStart, setSelStart] = useState<number | null>(null);
  const [selEnd, setSelEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dialog, setDialog] = useState<{
    type: "event" | "book";
    x: number;
    y: number;
    event?: ScheduleEvent;
    slot?: { start: string; end: string };
  } | null>(null);
  const selectionDate = date ?? new Date();

  // Build a set of booked slot indices
  const bookedSlots = new Set<number>();
  for (const ev of events) {
    const startMin = minutesFromDayStart(ev.start);
    const endMin = minutesFromDayStart(ev.end);
    for (let i = Math.floor(startMin / SLOT_MINUTES); i < Math.ceil(endMin / SLOT_MINUTES); i++) {
      bookedSlots.add(i);
    }
  }

  function slotToIso(slot: number): string {
    const mins = DAY_START_HOUR * 60 + slot * SLOT_MINUTES;
    const d = new Date(selectionDate);
    d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return d.toISOString();
  }

  const selMin = selStart != null && selEnd != null ? Math.min(selStart, selEnd) : null;
  const selMax = selStart != null && selEnd != null ? Math.max(selStart, selEnd) : null;

  // Past slot threshold — slots before this index are in the past. The day
  // comparison is on LOCAL calendar dates (see ``scheduleTime.ts``): with the
  // old UTC-string compare, a UTC+2 evening at/after 22:00 made "tomorrow"
  // look like the same UTC day as today and greyed its slots as past. We
  // FLOOR (not ceil) so the in-progress quarter (the slot "now" sits inside)
  // stays bookable — you can grab a room from the current time. The booking
  // start is then clamped to "now" (see ``clampStartToNow``) so a 15:04
  // booking of the 15:00 slot starts at 15:04, not 15:00.
  const pastSlotThreshold = computePastSlotThreshold(selectionDate);

  const hasConflict = selMin != null && selMax != null && (() => {
    for (let i = selMin; i <= selMax; i++) {
      if (bookedSlots.has(i)) return true;
    }
    return false;
  })();

  useEffect(() => {
    if (calendarRef.current) {
      const now = new Date();
      const scrollHour = isToday ? Math.max(0, now.getHours() - 1) : SCROLL_TO_HOUR;
      calendarRef.current.scrollTop = scrollHour * 60 * PX_PER_MINUTE;
    }
  }, [isToday, events]);

  // Global mouseup — end drag and show BookingDialog immediately
  useEffect(() => {
    const handleUp = (e: MouseEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      if (selStart == null || selEnd == null || !onBook) return;
      const sMin = Math.min(selStart, selEnd);
      const sMax = Math.max(selStart, selEnd);
      // Check for conflicts (booked or past)
      if (sMin < pastSlotThreshold) return;
      let conflict = false;
      for (let i = sMin; i <= sMax; i++) {
        if (bookedSlots.has(i)) { conflict = true; break; }
      }
      if (conflict) return;
      setDialog({
        type: "book",
        x: e.clientX,
        y: e.clientY,
        slot: { start: clampStartToNow(slotToIso(sMin)), end: slotToIso(sMax + 1) },
      });
    };
    window.addEventListener("mouseup", handleUp);
    return () => window.removeEventListener("mouseup", handleUp);
  }, [isDragging, selStart, selEnd, onBook, bookedSlots]);

  const hourLabels = Array.from(
    { length: DAY_END_HOUR - DAY_START_HOUR + 1 },
    (_, i) => DAY_START_HOUR + i,
  );

  return (
    <div className={className} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Day navigation */}
      {dateLabel && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: compact ? "8px 12px" : "8px 0",
          borderBottom: "1px solid rgba(148,163,184,0.15)",
        }}>
          {onPrevDay && (
            <button onClick={onPrevDay} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#94a3b8", fontSize: 16 }}>
              ‹
            </button>
          )}
          <div
            style={{ textAlign: "center", flex: 1, cursor: onDateChange ? "pointer" : undefined, position: "relative" }}
            onClick={onDateChange ? () => dateInputRef.current?.showPicker() : undefined}
          >
            <div style={{ fontSize: 13, fontWeight: 500, textDecoration: onDateChange ? "underline" : undefined, color: onDateChange ? "var(--mantine-color-blue-6, #228be6)" : undefined }}>{dateLabel}</div>
            {isToday && (
              <span style={{ fontSize: 10, border: "1px solid rgba(148,163,184,0.3)", borderRadius: 4, padding: "1px 6px" }}>
                Today
              </span>
            )}
            {onDateChange && (
              <input
                ref={dateInputRef}
                type="date"
                value={localDateKey(selectionDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    onDateChange(new Date(e.target.value + "T00:00:00"));
                  }
                }}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}
              />
            )}
          </div>
          {onNextDay && (
            <button onClick={onNextDay} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#94a3b8", fontSize: 16 }}>
              ›
            </button>
          )}
        </div>
      )}

      {/* Calendar grid */}
      <div ref={calendarRef} style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Hour labels */}
          <div style={{ flexShrink: 0, width: compact ? 32 : 40, position: "relative", height: TOTAL_MINUTES * PX_PER_MINUTE }}>
            {hourLabels.map((h) => (
              <div
                key={h}
                style={{
                  position: "absolute", top: (h - DAY_START_HOUR) * 60 * PX_PER_MINUTE - 6,
                  width: compact ? 28 : 36, textAlign: "right", paddingRight: 4,
                  fontSize: 11, color: "#94a3b8", lineHeight: 1,
                }}
              >
                {h === DAY_END_HOUR ? "" : `${String(h).padStart(2, "0")}:00`}
              </div>
            ))}
          </div>

          {/* Event area */}
          <div
            style={{
              position: "relative", flex: 1, minWidth: compact ? 120 : 200,
              height: TOTAL_MINUTES * PX_PER_MINUTE,
              borderLeft: "1px solid rgba(148,163,184,0.2)",
            }}
          >
            {/* Selectable slot grid */}
            {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
              const isBooked = bookedSlots.has(i);
              const isPast = i < pastSlotThreshold;
              const isSelected = selMin != null && selMax != null && i >= selMin && i <= selMax;
              const isInteractive = onBook && !isBooked && !isPast;
              const isHourBoundary = i % 4 === 0;

              let background: string;
              if (isSelected) {
                background = hasConflict ? "rgba(239,68,68,0.15)" : "rgba(59,130,246,0.25)";
              } else if (isPast) {
                background = "rgba(148,163,184,0.08)";
              } else if (isBooked) {
                background = "transparent";
              } else if (onBook) {
                background = "rgba(34,197,94,0.08)";
              } else {
                background = "transparent";
              }

              return (
                <div
                  key={`slot-${i}`}
                  style={{
                    position: "absolute", left: 0, right: 0,
                    top: i * SLOT_HEIGHT, height: SLOT_HEIGHT,
                    background,
                    borderBottom: isHourBoundary ? undefined : "1px solid rgba(148,163,184,0.06)",
                    cursor: isInteractive ? "crosshair" : "default",
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => {
                    if (!isInteractive) return;
                    e.preventDefault();
                    setSelStart(i);
                    setSelEnd(i);
                    setIsDragging(true);
                  }}
                  onMouseEnter={() => {
                    if (isDragging && !isBooked) setSelEnd(i);
                  }}
                />
              );
            })}

            {/* Hour grid lines */}
            {hourLabels.map((h) => (
              <div
                key={h}
                style={{
                  position: "absolute", left: 0, right: 0,
                  top: (h - DAY_START_HOUR) * 60 * PX_PER_MINUTE,
                  borderTop: "1px solid rgba(148,163,184,0.1)",
                }}
              />
            ))}

            {/* Free slots — clipped to current time on today */}
            {freeSlots.map((slot, i) => (
              <FreeSlotBlock
                key={i}
                slot={slot}
                durations={bookDurations}
                onBook={onBook ? (start, end, x, y) => {
                  // A priced slot never books on one tap: the chip opens the
                  // BookingDialog so the member sees the estimate and confirms.
                  if (estimateCharge(pricing, start, end) || priceNote?.(start, end) || bookingNotice?.(start, end)) {
                    setDialog({ type: "book", x, y, slot: { start, end } });
                  } else {
                    onBook(start, end, "Meeting", "");
                  }
                } : undefined}
                nowMinutes={isToday ? (new Date().getHours() * 60 + new Date().getMinutes() - DAY_START_HOUR * 60) : undefined}
              />
            ))}

            {/* Events */}
            {events.map((event) => (
              <EventBlock
                key={event.id}
                event={event}
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setDialog({ type: "event", event, x: rect.right + 8, y: rect.top });
                }}
              />
            ))}

            {/* Current time indicator */}
            {isToday && (() => {
              const now = new Date();
              const nowMinutes = now.getHours() * 60 + now.getMinutes() - DAY_START_HOUR * 60;
              if (nowMinutes < 0 || nowMinutes > TOTAL_MINUTES) return null;
              return (
                <div style={{ position: "absolute", left: 0, right: 0, top: nowMinutes * PX_PER_MINUTE, zIndex: 10, pointerEvents: "none" }}>
                  <div style={{ position: "absolute", left: -4, top: -4, width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                  <div style={{ height: 2, background: "#ef4444", opacity: 0.8 }} />
                </div>
              );
            })()}

            {events.length === 0 && freeSlots.length === 0 && (
              <div style={{ position: "absolute", top: 16, left: 8, fontSize: 12, color: "#94a3b8" }}>
                No events
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unified BookingDialog — handles both event details and slot booking */}
      {dialog && (
        <BookingDialog
          x={dialog.x}
          y={dialog.y}
          onDismiss={() => { setDialog(null); if (dialog.type === "book") { setSelStart(null); setSelEnd(null); } }}
          event={dialog.type === "event" ? dialog.event : undefined}
          onCancel={onCancel}
          bookingSlot={dialog.type === "book" ? dialog.slot : undefined}
          onBook={onBook ? (start, end, title, description) => { onBook(start, end, title, description); setSelStart(null); setSelEnd(null); setDialog(null); } : undefined}
          priceNote={priceNote}
          pricing={pricing}
          cancelNote={cancelNote}
          bookingNotice={bookingNotice}
        />
      )}
    </div>
  );
}
